import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, Eye, Calendar, CheckCircle, DollarSign, TrendingUp,
  ChevronRight, Sparkles, AlertCircle, Camera, Tag,
  Star, MessageSquare, Clock, RefreshCw, Car, ArrowUpRight, ArrowDownRight,
  Lightbulb, Target, Award, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { getVehiclePhotoUrl, handleVehicleImageError } from '../lib/imageUtils';
import { HostAppShell } from '../components/HostAppShell';

// Simple chart component for trends
const TrendChart = ({ data, type = 'bar', color = '#004B8D', height = 120 }) => {
  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center bg-slate-50 rounded-lg`} style={{ height }}>
        <p className="text-sm text-slate-400">No data yet</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.count || d.amount || 0));
  const barWidth = `${100 / Math.min(data.length, 30)}%`;

  return (
    <div className="relative" style={{ height }}>
      <div className="flex items-end justify-between h-full gap-0.5">
        {data.slice(-30).map((item, index) => {
          const value = item.count || item.amount || 0;
          const heightPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
          return (
            <div
              key={index}
              className="flex-1 min-w-[4px] rounded-t transition-all hover:opacity-80"
              style={{
                height: `${Math.max(heightPercent, 2)}%`,
                backgroundColor: color,
                opacity: 0.7 + (index / data.length) * 0.3
              }}
              title={`${item.date}: ${value}`}
            />
          );
        })}
      </div>
    </div>
  );
};

const HostAnalytics = () => {
  const { user, api, isHost, hasFeature, getSubscriptionTier } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [advanced, setAdvanced] = useState(null);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [period, setPeriod] = useState(30);
  
  const tier = getSubscriptionTier();
  const hasAdvanced = hasFeature('advanced_analytics');

  useEffect(() => {
    if (!isHost || !isHost()) {
      navigate('/host');
      return;
    }
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/host/analytics?days=${period}`);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
    
    // Fetch advanced analytics if entitled
    if (hasAdvanced) {
      setAdvancedLoading(true);
      try {
        const res = await api.get(`/host/analytics/advanced?days=${period}`);
        setAdvanced(res.data);
      } catch {
        // Silently fail — Pro sections show loading state or empty
      }
      setAdvancedLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-50 border-red-200 text-red-700';
      case 'medium': return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'low': return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'info': return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      default: return 'bg-slate-50 border-slate-200 text-slate-700';
    }
  };

  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'photos': return Camera;
      case 'pricing': return Tag;
      case 'reviews': return Star;
      case 'availability': return Calendar;
      case 'optimization': return Target;
      case 'success': return Award;
      default: return Lightbulb;
    }
  };

  if (loading) {
    return (
      <HostAppShell title="Analytics" activeId="analytics">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-[#004B8D] mx-auto mb-3" />
            <p className="text-slate-600">Loading analytics...</p>
          </div>
        </div>
      </HostAppShell>
    );
  }

  const summary = analytics?.summary || {};
  const conversions = analytics?.conversions || {};
  const trends = analytics?.trends || {};
  const vehicles = analytics?.vehicles || [];
  const recommendations = analytics?.recommendations || [];

  return (
    <HostAppShell title="Analytics" activeId="analytics">
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
        {/* Period Filter */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Track your vehicle performance and earnings</p>
          <div className="flex items-center gap-2">
            {[7, 30, 90, 0].map((days) => (
              <Button
                key={days}
                variant={period === days ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriod(days)}
                className={`text-xs sm:text-sm ${period === days ? 'bg-[#004B8D]' : ''}`}
              >
                {days === 0 ? 'All' : `${days}d`}
              </Button>
            ))}
          </div>
        </div>
        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="analytics-summary-cards">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-slate-500 font-medium">Views</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="total-views">
                {summary.total_views?.toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-slate-500 font-medium">Requests</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="total-requests">
                {summary.total_requests || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-slate-500 font-medium">Approved</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="approved-bookings">
                {summary.approved_bookings || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Car className="w-4 h-4 text-indigo-500" />
                <span className="text-xs text-slate-500 font-medium">Trips</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="completed-trips">
                {summary.completed_trips || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-green-500" />
                <span className="text-xs text-slate-500 font-medium">Gross</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="gross-earnings">
                {formatCurrency(summary.gross_earnings || 0)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 border-0">
            <CardContent className="p-4 text-white">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium opacity-90">Net Earnings</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold" data-testid="net-earnings">
                {formatCurrency(summary.net_earnings || 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Conversion Funnel */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-[#004B8D]" />
              Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
              <div className="text-center flex-1">
                <p className="text-3xl font-bold text-slate-900">{summary.total_views || 0}</p>
                <p className="text-sm text-slate-500">Views</p>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <ChevronRight className="w-5 h-5" />
                <span className="text-sm font-medium text-[#004B8D]" data-testid="view-to-request-rate">
                  {conversions.view_to_request || 0}%
                </span>
              </div>
              <div className="text-center flex-1">
                <p className="text-3xl font-bold text-slate-900">{summary.total_requests || 0}</p>
                <p className="text-sm text-slate-500">Requests</p>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <ChevronRight className="w-5 h-5" />
                <span className="text-sm font-medium text-[#004B8D]" data-testid="request-to-approved-rate">
                  {conversions.request_to_approved || 0}%
                </span>
              </div>
              <div className="text-center flex-1">
                <p className="text-3xl font-bold text-slate-900">{summary.approved_bookings || 0}</p>
                <p className="text-sm text-slate-500">Approved</p>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <ChevronRight className="w-5 h-5" />
                <span className="text-sm font-medium text-[#004B8D]" data-testid="approved-to-completed-rate">
                  {conversions.approved_to_completed || 0}%
                </span>
              </div>
              <div className="text-center flex-1">
                <p className="text-3xl font-bold text-emerald-600">{summary.completed_trips || 0}</p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trend Charts */}
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-500" />
                Views Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart data={trends.views} color="#3b82f6" />
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-500" />
                Bookings Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart data={trends.bookings} color="#f59e0b" />
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                Earnings Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart data={trends.earnings} color="#10b981" />
            </CardContent>
          </Card>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                How to Improve Bookings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recommendations">
                {recommendations.map((rec, index) => {
                  const Icon = getRecommendationIcon(rec.type);
                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-xl border ${getPriorityColor(rec.priority)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white/60 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="font-medium text-sm mb-1">{rec.title}</h4>
                          <p className="text-xs opacity-80">{rec.message}</p>
                          {rec.vehicles && rec.vehicles.length > 0 && (
                            <p className="text-xs mt-2 opacity-60">
                              Vehicles: {rec.vehicles.join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicle Performance Table */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Car className="w-5 h-5 text-[#004B8D]" />
              Vehicle Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {vehicles.length === 0 ? (
              <div className="text-center py-12">
                <Car className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No vehicles listed yet</p>
                <Button className="mt-4" onClick={() => navigate('/host/vehicles/new')}>
                  List Your First Vehicle
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="vehicle-performance-list">
                {vehicles.map((v) => (
                  <Link
                    key={v.vehicle_id}
                    to={`/host/analytics/vehicle/${v.vehicle_id}`}
                    className="block"
                  >
                    <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:border-[#004B8D]/30 hover:bg-slate-50 transition-all">
                      {/* Vehicle Photo */}
                      <div className="w-16 h-12 sm:w-20 sm:h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        {v.photo ? (
                          <img
                            src={getVehiclePhotoUrl(v.photo)}
                            alt={v.name}
                            className="w-full h-full object-cover"
                            onError={handleVehicleImageError}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Car className="w-6 h-6 text-slate-300" />
                          </div>
                        )}
                      </div>

                      {/* Vehicle Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-slate-900 truncate">{v.name}</h4>
                          {v.status === 'inactive' && (
                            <Badge variant="outline" className="text-xs bg-slate-100">Inactive</Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {v.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {v.booking_requests}
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> {v.completed_trips}
                          </span>
                          {v.avg_rating && (
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {v.avg_rating}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="hidden sm:flex items-center gap-6 text-right">
                        <div>
                          <p className="text-xs text-slate-500">Conversion</p>
                          <p className="font-semibold text-slate-900">{v.conversion_rate}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Net Earnings</p>
                          <p className="font-semibold text-emerald-600">{formatCurrency(v.net_earnings)}</p>
                        </div>
                      </div>

                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* ENHANCED ANALYTICS — Pro+ tier only                          */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        
        {!hasAdvanced && (
          <Card className="border-dashed border-violet-200 bg-violet-50/30" data-testid="analytics-upgrade-teaser">
            <CardContent className="p-6 text-center">
              <BarChart3 className="w-8 h-8 text-violet-400 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-800">Enhanced Analytics</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                Unlock per-vehicle performance, revenue trends, utilisation insights, and optimisation recommendations with Pro.
              </p>
              <Button className="mt-4 bg-violet-600 hover:bg-violet-700 text-sm h-9" onClick={() => navigate('/host/plan')} data-testid="upgrade-analytics-btn">
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        )}

        {hasAdvanced && advanced && (
          <>
            {/* Revenue Trend */}
            <Card className="border-slate-200" data-testid="enhanced-revenue-trend">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#004B8D]" />
                  Revenue Trend
                  <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px] border">Pro</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
                  <div>
                    <p className="text-xs text-slate-500">This period</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid="current-period-revenue">
                      {formatCurrency(advanced.trends?.recent_period_revenue || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Previous period</p>
                    <p className="text-lg font-semibold text-slate-500">
                      {formatCurrency(advanced.trends?.previous_period_revenue || 0)}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold ${
                    advanced.trends?.trend_direction === 'up' ? 'bg-green-100 text-green-700' : 
                    advanced.trends?.trend_direction === 'down' ? 'bg-red-100 text-red-700' : 
                    'bg-slate-100 text-slate-600'
                  }`} data-testid="trend-indicator">
                    {advanced.trends?.trend_direction === 'up' ? <ArrowUpRight className="w-4 h-4" /> : 
                     advanced.trends?.trend_direction === 'down' ? <ArrowDownRight className="w-4 h-4" /> : null}
                    {advanced.trends?.trend_percent || 0}%
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Key Metrics Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="enhanced-key-metrics">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Avg per Booking</p>
                  <p className="text-xl font-bold text-slate-900" data-testid="avg-earning-per-booking">
                    {formatCurrency(advanced.summary?.avg_earning_per_booking || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Active Vehicles</p>
                  <p className="text-xl font-bold text-slate-900">{advanced.summary?.active_vehicle_count || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">With Bookings</p>
                  <p className="text-xl font-bold text-green-600">{advanced.utilisation?.vehicles_with_bookings || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Without Bookings</p>
                  <p className="text-xl font-bold text-amber-600">{advanced.utilisation?.vehicles_without_bookings || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Best / Worst Performers */}
            {(advanced.utilisation?.most_active_vehicle || advanced.utilisation?.least_active_vehicle) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="enhanced-performers">
                {advanced.utilisation?.most_active_vehicle && (
                  <Card className="border-green-200 bg-green-50/30">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Top Performer</span>
                      </div>
                      <p className="font-semibold text-slate-900" data-testid="best-performer-name">{advanced.utilisation.most_active_vehicle.vehicle_name}</p>
                      <p className="text-sm text-slate-600">
                        {formatCurrency(advanced.utilisation.most_active_vehicle.revenue_total)} from {advanced.utilisation.most_active_vehicle.completed_count} trips
                      </p>
                    </CardContent>
                  </Card>
                )}
                {advanced.utilisation?.least_active_vehicle && (
                  <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Needs Attention</span>
                      </div>
                      <p className="font-semibold text-slate-900" data-testid="worst-performer-name">{advanced.utilisation.least_active_vehicle.vehicle_name}</p>
                      <p className="text-sm text-slate-600">
                        {advanced.utilisation.least_active_vehicle.completed_count === 0 ? 'No completed bookings yet' : 
                         `${formatCurrency(advanced.utilisation.least_active_vehicle.revenue_total)} from ${advanced.utilisation.least_active_vehicle.completed_count} trips`}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Per-Vehicle Performance Table */}
            {advanced.vehicle_performance?.length > 0 && (
              <Card className="border-slate-200" data-testid="enhanced-vehicle-table">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Car className="w-4 h-4 text-[#004B8D]" />
                    Vehicle Performance
                  </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium">Vehicle</th>
                        <th className="text-right py-2 px-2 text-xs text-slate-500 font-medium">Bookings</th>
                        <th className="text-right py-2 px-2 text-xs text-slate-500 font-medium">Completed</th>
                        <th className="text-right py-2 px-2 text-xs text-slate-500 font-medium">Revenue</th>
                        <th className="text-right py-2 px-2 text-xs text-slate-500 font-medium">Avg/Trip</th>
                      </tr>
                    </thead>
                    <tbody>
                      {advanced.vehicle_performance.map(vp => (
                        <tr key={vp.vehicle_id} className="border-b border-slate-100 last:border-0">
                          <td className="py-2 px-2 font-medium text-slate-800 truncate max-w-[200px]">{vp.vehicle_name}</td>
                          <td className="py-2 px-2 text-right text-slate-600">{vp.booking_count}</td>
                          <td className="py-2 px-2 text-right text-slate-600">{vp.completed_count}</td>
                          <td className="py-2 px-2 text-right font-semibold text-green-600">{formatCurrency(vp.revenue_total)}</td>
                          <td className="py-2 px-2 text-right text-slate-600">{vp.avg_revenue_per_booking > 0 ? formatCurrency(vp.avg_revenue_per_booking) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Premium Insights */}
            {advanced.premium_insights && advanced.premium_insights.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/10" data-testid="premium-insights-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    Optimisation Insights
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] border">Premium</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {advanced.premium_insights.map((insight, idx) => (
                    <div key={idx} className={`flex items-start gap-3 p-3 rounded-xl border ${
                      insight.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                      insight.severity === 'opportunity' ? 'bg-blue-50 border-blue-200' :
                      'bg-green-50 border-green-200'
                    }`} data-testid={`insight-${idx}`}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        insight.severity === 'warning' ? 'bg-amber-100' :
                        insight.severity === 'opportunity' ? 'bg-blue-100' :
                        'bg-green-100'
                      }`}>
                        <Lightbulb className={`w-3.5 h-3.5 ${
                          insight.severity === 'warning' ? 'text-amber-600' :
                          insight.severity === 'opportunity' ? 'text-blue-600' :
                          'text-green-600'
                        }`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{insight.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{insight.message}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Premium insights teaser for Pro (not Premium) */}
            {tier === 'pro' && !advanced.premium_insights && (
              <Card className="border-dashed border-amber-200 bg-amber-50/20" data-testid="premium-insights-teaser">
                <CardContent className="p-6 text-center">
                  <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-800">Optimisation Insights</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                    Get actionable recommendations to improve your fleet performance with Premium.
                  </p>
                  <Button className="mt-4 bg-amber-600 hover:bg-amber-700 text-sm h-9" onClick={() => navigate('/host/plan')} data-testid="upgrade-premium-btn">
                    Upgrade to Premium
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {hasAdvanced && advancedLoading && !advanced && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          </div>
        )}
      </div>
    </HostAppShell>
  );
};

export default HostAnalytics;
